import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, SideDrawer, withStyles, type AccountUserRole } from 'vopay';
import PermissionsSearchField from '../UserPermissionsDrawer/PermissionsSearchField';
import useRolePermissionsCatalog from './useRolePermissionsCatalog';
import styles from './RolePermissionsViewDrawer.module.scss';

const cn = withStyles(styles);

type RolePermissionsViewDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  onClose: () => void;
};

type GroupedPermissions = {
  groupName: string;
  items: { permissionKey: string; label: string }[];
}[];

// Read-only list of the permissions a role has enabled, grouped by category. The labels come from the
// permission catalog, so only its keys and labels are used.
export default function RolePermissionsViewDrawer({ isOpen, role, onClose }: RolePermissionsViewDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.RolePermissionsViewDrawer' });
  const [searchTerm, setSearchTerm] = useState('');
  const { catalog, isFetching, fetchCatalog } = useRolePermissionsCatalog();

  // The catalog is the same for every role, so it is fetched once and kept.
  useEffect(() => {
    if (!isOpen || Object.keys(catalog).length > 0) return;

    // The hook has logged; the drawer just shows no permissions.
    fetchCatalog().catch(() => {});
  }, [isOpen, fetchCatalog]);

  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const grouped = useMemo(() => {
    if (!role) return [];

    const owned = new Set(role.Permissions);
    const needle = searchTerm.trim().toLowerCase();
    const groups: GroupedPermissions = [];

    Object.entries(catalog).forEach(([groupName, group]) => {
      const items = Object.entries(group)
        .filter(([permissionKey]) => owned.has(permissionKey))
        .map(([permissionKey, item]) => ({ permissionKey, label: item.label }))
        .filter(({ label }) => (needle ? label.toLowerCase().includes(needle) : true));

      if (items.length > 0) groups.push({ groupName, items });
    });

    return groups;
  }, [catalog, role, searchTerm]);

  if (!role) return null;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Permissions' })}
      headerFullWidthContent={
        <PermissionsSearchField
          name="permissionsViewSearch"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('searchPlaceholder', { defaultValue: 'Search Permissions' })}
        />
      }
    >
      <div className={cn('body')}>
        {isFetching && <BodyText weight="md">{t('loading', { defaultValue: 'Loading permissions...' })}</BodyText>}

        {!isFetching && grouped.length === 0 && (
          <BodyText weight="md" className={cn('emptyState')}>
            {searchTerm
              ? t('noMatches', { defaultValue: 'No permissions match your search.' })
              : t('noPermissions', { defaultValue: 'This role has no permissions enabled.' })}
          </BodyText>
        )}

        {!isFetching &&
          grouped.map((group) => (
            <div key={group.groupName} className={cn('group')}>
              <h3 className={cn('groupHeading')}>{group.groupName}</h3>

              <ul className={cn('permissionList')}>
                {group.items.map((item) => (
                  <li key={item.permissionKey} className={cn('permissionItem')}>
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>
    </SideDrawer>
  );
}
